import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { getFavorites } from 'flow/actions/favorites-actions';
import { reloadNonAdminPbx, reloadPbx } from 'flow/actions/pbx-actions';
import {
    firstIndexToFocus,
    listItemKey,
    uniqueRowKeys,
} from 'flow/components/nav/list-keys/list-key-action';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import useRowSelection from 'flow/components/nav/list-keys/useRowSelection';
import PbxListSkeleton from 'flow/components/loaders/PbxListSkeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import {
    selectDefaultPBXKey,
    selectPbxIsLoaded,
} from 'flow/selectors/queue-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import NoMatchOnSearch from './empty-states/NoMatchOnSearch';
import {
    DepartmentState,
    PbxListDepartment,
    PbxListItem,
    pbxListRowHeight,
    pbxListRowKey,
    rowIsFocusable,
} from './pbx-list-rows';
import PbxListWindowRow, { PbxListItemData } from './PbxListRow';
import usePbxListRows from './usePbxListRows';

const PBX_OPEN = 'pbxOpen';

// A default array would be a new one on every render, and the rows are
// memoized on it.
const ALL_DEPARTMENTS: PbxListDepartment[] = [
    'supervisorView',
    'favorites',
    'ivr',
    'queues',
    'liveChats',
    'voicemails',
    'connections',
    'aiCoworkers',
];

interface Props {
    selectedPbx: string;
    onItemSelected: Function;
    filterText: string;
    selectDefaultItem?: boolean;
    enabledDepartments?: PbxListDepartment[];
    enableCallButton?: boolean;
    /**
     * The selection is the page that is open. A picker sets it to false: there
     * the selection is the Accept target.
     */
    marksOpenPage?: boolean;
}

const savePbxState = (department: keyof DepartmentState, isOpen: boolean) => {
    if (browserSupportStorage()) {
        const departmentStates = wrappedLocalStorage.isSet(PBX_OPEN)
            ? JSON.parse(wrappedLocalStorage.getItem(PBX_OPEN))
            : {};
        departmentStates[department] = isOpen;
        wrappedLocalStorage.setItem(PBX_OPEN, JSON.stringify(departmentStates));
    }
};

const PbxList_TW = ({
    selectedPbx,
    onItemSelected,
    filterText,
    selectDefaultItem = true,
    enabledDepartments = ALL_DEPARTMENTS,
    enableCallButton = true,
    marksOpenPage = true,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const isAdmin = useAppSelector(selectIsAdmin);

    const defaultPBXKey = useAppSelector(selectDefaultPBXKey);
    const pbxIsLoaded = useAppSelector(selectPbxIsLoaded);

    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<VariableSizeList>(null);

    const [selected, setSelected] = useState(selectedPbx);

    const departmentStates = wrappedLocalStorage.isSet(PBX_OPEN)
        ? JSON.parse(wrappedLocalStorage.getItem(PBX_OPEN))
        : {};

    const [departmentExpanded, setDepartmentExpanded] =
        useState<DepartmentState>({
            favorites: departmentStates.favorites ?? true,
            refers: departmentStates.refers ?? true,
            queues: departmentStates.queues ?? true,
            channels: departmentStates.channels ?? true,
            voicemails: departmentStates.voicemails ?? true,
            connections: departmentStates.connections ?? true,
            aiCoworkers: departmentStates.aiCoworkers ?? true,
        });

    const rows = usePbxListRows({
        filterText,
        departmentExpanded,
        enabledDepartments,
    });
    // react-window keys each row element by its key, so focus and the row's
    // own state, such as an open menu, stay with its item when rows come in
    // above it. The Tab stop and the selection name their row by it too.
    const rowKeys = useMemo(() => uniqueRowKeys(rows, pbxListRowKey), [rows]);

    useEffect(() => {
        dispatch(getFavorites());
        dispatch(isAdmin ? reloadPbx() : reloadNonAdminPbx());
    }, [dispatch, isAdmin]);

    // A favourite queue is in two rows, and only one shows the selection. The
    // list shares the rule with the contact list (useRowSelection).
    const noChoiceIndex = useCallback(
        () =>
            rows.findIndex(
                (row) => row.kind === 'item' && row.key === selected,
            ),
        [rows, selected],
    );
    const { selectionStore, findSelectedIndex, chooseRow } = useRowSelection({
        rowKeys,
        openItemKey: selected,
        noChoiceIndex,
    });

    const selectedOrFirstIndex = useCallback(() => {
        const selectedIndex = findSelectedIndex();
        return selectedIndex === -1
            ? firstIndexToFocus(rows, rowIsFocusable)
            : selectedIndex;
    }, [rows, findSelectedIndex]);

    const {
        tabStopStore,
        onFocus,
        onKeyDown,
        onItemsRendered,
        onRowUnmountWithFocus,
    } = useListTabStop({
        containerRef,
        listRef,
        rows,
        rowKeys,
        rowIsFocusable,
        entryIndex: selectedOrFirstIndex,
    });

    useEffect(() => {
        if (selectDefaultItem && selectedPbx === undefined) {
            setSelected(defaultPBXKey);
        }
    }, [selectDefaultItem, selectedPbx, defaultPBXKey]);

    // The page can change without a click in the list, as by Back.
    useLayoutEffect(() => {
        if (selectedPbx) {
            setSelected(selectedPbx);
        }
    }, [selectedPbx]);

    const onSelect = useCallback(
        (index: number, key: string, item?: PbxListItem) => {
            setSelected(key);
            chooseRow(index, key);

            onItemSelected?.(key, item);
        },
        [onItemSelected, chooseRow],
    );

    // One function for the list's life, so a section title that does not
    // change does not render again when another section folds.
    const onToggleSection = useCallback(
        (department: keyof DepartmentState, isOpen: boolean) => {
            setDepartmentExpanded((previousExpanded) => ({
                ...previousExpanded,
                [department]: !isOpen,
            }));
            savePbxState(department, !isOpen);
        },
        [],
    );

    // A page without a key, such as a conference, has no row: the selection
    // is then the default item, not the open page.
    const selectionIsOpenPage = marksOpenPage && !!selectedPbx;
    const itemData = useMemo<PbxListItemData>(
        () => ({
            rows,
            selectionStore,
            tabStopStore,
            rowKeys,
            enableCallButton,
            selectionIsOpenPage,
            onSelect,
            onToggleSection,
            onRowUnmountWithFocus,
        }),
        [
            rows,
            selectionStore,
            tabStopStore,
            rowKeys,
            enableCallButton,
            selectionIsOpenPage,
            onSelect,
            onToggleSection,
            onRowUnmountWithFocus,
        ],
    );

    // Each list above turns a missing slice into an empty List, so an empty
    // list cannot tell "not fetched yet" from "nothing here".
    //
    // This only catches a cold start, where no part of the pbx has answered.
    // It does not cover the window while the slices arrive one by one: queues
    // is already fetched by the session bootstrap (app-actions getClientDTO)
    // and by AppToolbar, so this is true again long before the list opens,
    // while voicemails are only ever fetched below. Closing that window needs
    // the store to track pbx requests in flight.
    if (!pbxIsLoaded) {
        return <PbxListSkeleton />;
    }

    // The pbx slices answer one at a time, so an empty list on its own does
    // not mean there is nothing to show - the rest may still be on its way.
    // Only a search that matched nothing is worth telling the user about.
    // Same condition as ContactList.
    const searchFoundNothing =
        !isEmptyNullOrUndefined(filterText) && rows.length === 0;

    if (listRef.current) {
        // Reset the whole list item height cache here. We could calculate from which index to
        // clear the cache here but that would add extra complexity and will be hard to maintain,
        // so we go with this approach and will optimize it if we see that it's necessary.
        listRef.current.resetAfterIndex(0, false);
    }

    return (
        <div // NOSONAR
            // Not a control: see useListTabStop.
            ref={containerRef}
            className="left-nav-scroll-wrapper h-full flex-auto"
            data-context-menu="false"
            onFocus={onFocus}
            onKeyDown={onKeyDown}
        >
            {searchFoundNothing ? (
                <NoMatchOnSearch reserveScrollbarGutter={true}>
                    {i18next.t('PBX.SearchEmpty.Description', {
                        filterText,
                    })}
                </NoMatchOnSearch>
            ) : (
                <AutoSizer className="size-full" disableWidth={true}>
                    {({ height }) => (
                        <VariableSizeList
                            ref={listRef}
                            overscanCount={7}
                            height={height}
                            width="100%"
                            itemData={itemData}
                            itemCount={rows.length}
                            itemKey={listItemKey}
                            itemSize={(index) => pbxListRowHeight(rows[index])}
                            onItemsRendered={onItemsRendered}
                            className="scrollbar left-nav-scroll w-full"
                            style={{
                                overflowY: 'scroll',
                                direction: undefined,
                            }}
                        >
                            {PbxListWindowRow}
                        </VariableSizeList>
                    )}
                </AutoSizer>
            )}
        </div>
    );
};

export default PbxList_TW;
